import React, { useState, useEffect, useContext, useRef } from 'react';
import { API_BASE } from '../../config';
import { AuthContext } from '../../App';
import AINutritionistChat from '../AINutritionistChat';
import { PantryItemForm } from '../PantryForms';
import BarcodeLookupModal from '../BarcodeLookupModal';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { CitationsBlock } from '../Citations';
import EdamamAttribution from '../EdamamAttribution';
import { useCreditSpend } from '../../contexts/CreditSpendContext';
import { useConfirm } from '../ConfirmProvider';

function DashboardTab({
  fetchWithAuth,
  user,
  setShowHealthSummary,
  setShowHydrationModal,
  setShowAIAssistant,
  setActiveTab
}) {
  const { logout } = useContext(AuthContext);
  const confirm = useConfirm();
  const { spend } = useCreditSpend();  // #77 vision_meal_scan = 1 credit (chip on Upload Photo, low-balance confirm)
  // Local state for barcode modals (replaces broken App.jsx BarcodeScanner)
  const [showPantryItemForm, setShowPantryItemForm] = useState(false);

  // #81 personalized launcher — order + emphasis from launcher_config (null = static v1 default)
  const [launcherOrder, setLauncherOrder] = useState(null);
  const [launcherEmph, setLauncherEmph] = useState(() => new Set());
  // Loaded ONCE per mount. fetchWithAuth is a new function on every App render, so keying the effect on it re-fetched
  // the launcher ~5 times per page load (backend log, 2026-10-22); the ref keeps the latest one without re-running.
  const fetchWithAuthRef = useRef(fetchWithAuth);
  fetchWithAuthRef.current = fetchWithAuth;
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetchWithAuthRef.current(`${API_BASE}/api/dashboard/launcher`);
        // Always consume the body, even when abandoning it: an unread no-store reply never finishes loading in
        // Chromium (D11, proven on EC2Dev 2026-10-22).
        const body = await r.text();
        if (!r.ok || !alive) return;
        const d = JSON.parse(body);
        if (d.personalized && Array.isArray(d.tile_keys) && d.tile_keys.length) {
          setLauncherOrder(d.tile_keys);
          setLauncherEmph(new Set(d.emphasized || []));
        }
      } catch { /* fall back to the static default order */ }
    })();
    return () => { alive = false; };
  }, []);

  // Dashboard Stats State
  const [dashboardStats, setDashboardStats] = useState({
    daily_intake: { value: 0, goal: 2000, percent: 0, change: '', has_data: false, prompt: null },
    protein: { value: 0, goal: 120, percent: 0, status: '', has_data: false, prompt: null },
    hydration: { value: 0, goal: 2500, percent: 0, glasses_remaining: 8, has_data: false, prompt: null },
    healist_score: { value: 0, change: '', period: '', grade: '', has_data: false }
  });

  // Today's Meals State (enhanced for Layout C)
  const [todaysMeals, setTodaysMeals] = useState([]);
  const [hasMealPlan, setHasMealPlan] = useState(false);
  const [mealPlanPrompt, setMealPlanPrompt] = useState(null);
  const [mealPlanId, setMealPlanId] = useState(null);
  const [mealPlanName, setMealPlanName] = useState('');
  const [mealTotals, setMealTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [mealGoals, setMealGoals] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 65 });

  // Today's Nutrition UI State
  const [expandedMeals, setExpandedMeals] = useState(new Set());
  const [selectedMeal, setSelectedMeal] = useState(null);

  // Today's Nutrition Modals
  const [showAllDetailsModal, setShowAllDetailsModal] = useState(false);
  const [showMealDetailsModal, setShowMealDetailsModal] = useState(false);
  const [showAlternateModal, setShowAlternateModal] = useState(false);
  const [showRecipePickerModal, setShowRecipePickerModal] = useState(false);
  const [showPhotoCaptureModal, setShowPhotoCaptureModal] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showAddMealModal, setShowAddMealModal] = useState(false);
  const [showCloseDayModal, setShowCloseDayModal] = useState(false);

  // Recipe Picker State
  const [userRecipes, setUserRecipes] = useState([]);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [recipeFilterType, setRecipeFilterType] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipesLoading, setRecipesLoading] = useState(false);

  // Photo Capture State
  const [photoAnalyzing, setPhotoAnalyzing] = useState(false);
  const [photoResults, setPhotoResults] = useState(null);

  // All Details Modal State
  const [allMealDetails, setAllMealDetails] = useState([]);
  const [allDetailsLoading, setAllDetailsLoading] = useState(false);

  // Success Animation State
  const [showMealSuccess, setShowMealSuccess] = useState(false);
  const [mealSuccessMessage, setMealSuccessMessage] = useState('');
  // Mark All Consumed Result Modal
  const [showConsumedResult, setShowConsumedResult] = useState(false);
  const [consumedResult, setConsumedResult] = useState(null);

  // Loading States
  const [statsLoading, setStatsLoading] = useState(true);
  const [mealsLoading, setMealsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [syncingDevices, setSyncingDevices] = useState(false);

  // Reactivation state (Phase C)
  const [userMealPlans, setUserMealPlans] = useState([]);
  const [selectedReactivatePlanId, setSelectedReactivatePlanId] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [markingAllConsumed, setMarkingAllConsumed] = useState(false);

  // Stat Detail Modal State
  const [statModal, setStatModal] = useState({
    type: null, // 'hydration', 'calories', 'protein', 'score'
    days: 7,
    loading: false,
    data: null
  });

  // Load Stat Modal Data
  const loadStatModalData = async (type, days = 7) => {
    setStatModal(prev => ({ ...prev, type, days, loading: true, data: null }));

    try {
      let endpoint = '';
      if (type === 'hydration') endpoint = `${API_BASE}/api/dashboard/history/hydration?days=${days}`;
      else if (type === 'calories') endpoint = `${API_BASE}/api/dashboard/history/calories?days=${days}`;
      else if (type === 'protein') endpoint = `${API_BASE}/api/dashboard/history/protein?days=${days}`;
      else if (type === 'score') endpoint = `${API_BASE}/api/dashboard/history/healist-score`;

      const response = await fetchWithAuth(endpoint);
      if (response.ok) {
        const data = await response.json();
        setStatModal(prev => ({ ...prev, loading: false, data }));
      } else {
        setStatModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Failed to load stat modal data:', err);
      setStatModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeStatModal = () => {
    setStatModal({ type: null, days: 7, loading: false, data: null });
  };

  const handleDaysChange = (newDays) => {
    loadStatModalData(statModal.type, newDays);
  };

  // Load dashboard data on mount
  useEffect(() => {
    loadDashboardStats();
    loadTodaysMeals();
  }, []);

  const loadDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/stats`);
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
        setHasMealPlan(data.has_meal_plan);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTodaysMeals = async () => {
    setMealsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/todays-meals`);
      if (response.ok) {
        const data = await response.json();
        setTodaysMeals(data.meals || []);
        setHasMealPlan(data.has_meal_plan);
        setMealPlanPrompt(data.prompt);
      }
    } catch (error) {
      console.error('Failed to load today\'s meals:', error);
    } finally {
      setMealsLoading(false);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - ACCORDION HANDLERS
  // ==========================================================================

  const toggleMealExpand = (scheduleItemId) => {
    setExpandedMeals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scheduleItemId)) {
        newSet.delete(scheduleItemId);
      } else {
        newSet.add(scheduleItemId);
      }
      return newSet;
    });
  };

  // ==========================================================================
  // TODAY'S NUTRITION - MEAL ACTIONS
  // ==========================================================================

  const handleMealAction = async (meal, action, additionalData = {}) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/meal-action`, {
        method: 'POST',
        body: JSON.stringify({
          schedule_item_id: meal.schedule_item_id,
          action: action,
          ...additionalData
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Reload meals to get updated data
        await loadTodaysMeals();

        // Show success message
        if (action === 'skip') {
          showMealSuccessAnimation(`${meal.name} skipped`);
        } else if (action === 'half') {
          showMealSuccessAnimation(result.half_portion ? 'Half portion enabled' : 'Full portion restored');
        } else if (action === 'log') {
          showMealSuccessAnimation(`${meal.name} logged as eaten`);
        }

        return result;
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update meal');
        return null;
      }
    } catch (error) {
      console.error('Meal action error:', error);
      alert('Failed to update meal. Please try again.');
      return null;
    }
  };

  const handleSkipMeal = async (meal) => {
    if (await confirm({
      title: 'Skip this meal?',
      message: `${meal.name} won't count toward your daily totals.`,
      confirmLabel: 'Skip Meal',
    })) {
      handleMealAction(meal, 'skip');
    }
  };

  const handleToggleHalfPortion = (meal) => {
    handleMealAction(meal, 'half');
  };

  const handleLogMealEaten = (meal) => {
    handleMealAction(meal, 'log');
  };

  // ==========================================================================
  // TODAY'S NUTRITION - VIEW DETAILS
  // ==========================================================================

  const handleViewMealDetails = async (meal) => {
    setSelectedMeal(meal);
    setShowMealDetailsModal(true);
  };

  const handleViewAllDetails = async () => {
    setAllDetailsLoading(true);
    setShowAllDetailsModal(true);

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/meal-details-all`);
      if (response.ok) {
        const data = await response.json();
        setAllMealDetails(data.meals || []);
      }
    } catch (error) {
      console.error('Failed to load all meal details:', error);
    } finally {
      setAllDetailsLoading(false);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - ALTERNATE MEAL FLOW
  // ==========================================================================

  const handleOpenAlternate = (meal) => {
    setSelectedMeal(meal);
    setShowAlternateModal(true);
  };

  const handleSelectAlternateMethod = (method) => {
    setShowAlternateModal(false);

    if (method === 'recipe') {
      loadUserRecipes();
      setShowRecipePickerModal(true);
    } else if (method === 'photo') {
      setPhotoResults(null);
      setShowPhotoCaptureModal(true);
    } else if (method === 'barcode') {
      setShowBarcodeModal(true);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - RECIPE PICKER
  // ==========================================================================

  const loadUserRecipes = async (mealType = null) => {
    setRecipesLoading(true);
    try {
      let url = `${API_BASE}/api/dashboard/user-recipes?limit=30`;
      if (mealType && mealType !== 'all') {
        url += `&meal_type=${mealType}`;
      }
      if (recipeSearchTerm) {
        url += `&search=${encodeURIComponent(recipeSearchTerm)}`;
      }

      const response = await fetchWithAuth(url);
      if (response.ok) {
        const data = await response.json();
        setUserRecipes(data.recipes || []);
      }
    } catch (error) {
      console.error('Failed to load recipes:', error);
    } finally {
      setRecipesLoading(false);
    }
  };

  const handleRecipeFilterChange = (filterType) => {
    setRecipeFilterType(filterType);
    loadUserRecipes(filterType);
  };

  const handleRecipeSearch = (e) => {
    setRecipeSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.recipeSearchTimeout);
    window.recipeSearchTimeout = setTimeout(() => {
      loadUserRecipes(recipeFilterType);
    }, 300);
  };

  const handleSelectRecipe = (recipe) => {
    setSelectedRecipe(recipe);
  };

  const handleConfirmRecipeSelection = async () => {
    if (!selectedRecipe || !selectedMeal) return;

    const result = await handleMealAction(selectedMeal, 'alternate', {
      substitute_recipe_id: selectedRecipe.id
    });

    if (result) {
      setShowRecipePickerModal(false);
      setSelectedRecipe(null);
      showMealSuccessAnimation(`Replaced with ${selectedRecipe.name}`);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - PHOTO CAPTURE
  // ==========================================================================

  const handlePhotoCapture = async (imageBase64) => {
    setPhotoAnalyzing(true);

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/vision/analyze-meal`, {
        method: 'POST',
        body: JSON.stringify({
          image_base64: imageBase64,
          meal_type: selectedMeal?.type?.toLowerCase(),
          generate_recipe: false
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPhotoResults({
          detected_foods: data.detected_foods || [],
          total_calories: data.total_calories || 0,
          protein: data.total_protein || 0,
          carbs: data.total_carbs || 0,
          fat: data.total_fat || 0,
          dish_name: data.dish_name || 'Analyzed Meal',
          diet_labels: data.diet_labels || [],
          health_labels: data.health_labels || [],
          confidence: data.confidence || 0.85
        });
      } else {
        if (await handleAIError(response)) {
          return;
        }
        const error = await response.json();
        alert(error.detail || 'Failed to analyze photo');
      }
    } catch (error) {
      console.error('Photo analysis error:', error);
      alert('Failed to analyze photo. Please try again.');
    } finally {
      setPhotoAnalyzing(false);
    }
  };

  const handleConfirmPhotoMeal = async () => {
    if (!photoResults || !selectedMeal) return;

    const result = await handleMealAction(selectedMeal, 'alternate', {
      photo_meal_data: {
        dish_name: photoResults.dish_name,
        detected_foods: photoResults.detected_foods,
        total_calories: photoResults.total_calories,
        protein: photoResults.protein,
        carbs: photoResults.carbs,
        fat: photoResults.fat,
        diet_labels: photoResults.diet_labels
      }
    });

    if (result) {
      setShowPhotoCaptureModal(false);
      setPhotoResults(null);
      showMealSuccessAnimation(`Logged ${photoResults.dish_name}`);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - BARCODE SCAN
  // ==========================================================================

  const handleBarcodeScan = async (barcode, productData) => {
    if (!selectedMeal) return;

    const result = await handleMealAction(selectedMeal, 'alternate', {
      barcode_meal_data: {
        barcode: barcode,
        product_name: productData?.name || 'Scanned Product',
        brand: productData?.brand,
        calories: productData?.calories || 0,
        protein: productData?.protein || 0,
        carbs: productData?.carbs || 0,
        fat: productData?.fat || 0,
        serving_size: productData?.serving_size
      }
    });

    if (result) {
      setShowBarcodeModal(false);
      showMealSuccessAnimation(`Logged ${productData?.name || 'scanned item'}`);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - ADD MEAL/SNACK
  // ==========================================================================

  const handleOpenAddMeal = () => {
    setSelectedMeal(null);
    setShowAddMealModal(true);
  };

  const handleAddMealMethod = (method) => {
    setShowAddMealModal(false);
    // For "Add Meal", we don't have an existing schedule_item_id
    // This would create a new ad-hoc meal entry
    // For MVP, redirect to appropriate modal
    if (method === 'recipe') {
      loadUserRecipes();
      setShowRecipePickerModal(true);
    } else if (method === 'photo') {
      setPhotoResults(null);
      setShowPhotoCaptureModal(true);
    } else if (method === 'barcode') {
      setShowBarcodeModal(true);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - CLOSE TODAY
  // ==========================================================================

  const handleOpenCloseDay = () => {
    setShowCloseDayModal(true);
  };

  const handleConfirmCloseDay = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/close-today`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        setShowCloseDayModal(false);

        // Refresh dashboard
        await loadTodaysMeals();
        await loadDashboardStats();

        showMealSuccessAnimation(`Day closed! ${result.totals.calories} calories logged`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to close day');
      }
    } catch (error) {
      console.error('Close day error:', error);
      alert('Failed to close day. Please try again.');
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - MARK ALL CONSUMED + REACTIVATION (Phase C)
  // ==========================================================================

  const handleMarkAllConsumed = async () => {
    setMarkingAllConsumed(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/mark-all-consumed`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        await loadTodaysMeals();
        await loadDashboardStats();
        setConsumedResult(result);
        setShowConsumedResult(true);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to mark meals as consumed');
      }
    } catch (error) {
      console.error('Mark all consumed error:', error);
      alert('Failed to mark meals. Please try again.');
    } finally {
      setMarkingAllConsumed(false);
    }
  };

  const loadUserMealPlans = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/list`);
      if (response.ok) {
        const data = await response.json();
        setUserMealPlans(Array.isArray(data) ? data : data.plans || []);
      }
    } catch (error) {
      console.error('Failed to load meal plans:', error);
    }
  };

  const handleReactivatePlan = async () => {
    if (!selectedReactivatePlanId || selectedReactivatePlanId === 'create-new') {
      setActiveTab('nutrition');
      return;
    }
    setReactivating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/${selectedReactivatePlanId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: true })
      });
      if (response.ok) {
        await loadTodaysMeals();
        await loadDashboardStats();
        showMealSuccessAnimation('Meal plan reactivated!');
        setSelectedReactivatePlanId('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reactivate plan');
      }
    } catch (error) {
      console.error('Reactivate error:', error);
      alert('Failed to reactivate meal plan.');
    } finally {
      setReactivating(false);
    }
  };

  // ==========================================================================
  // TODAY'S NUTRITION - SUCCESS ANIMATION
  // ==========================================================================

  const showMealSuccessAnimation = (message) => {
    setMealSuccessMessage(message);
    setShowMealSuccess(true);
    setTimeout(() => {
      setShowMealSuccess(false);
    }, 2000);
  };

  // ==========================================================================
  // TODAY'S NUTRITION - HELPER FUNCTIONS
  // ==========================================================================

  const getStatusText = (status) => {
    switch (status) {
      case 'eaten': return '✓ Eaten';
      case 'skipped': return 'Skipped';
      case 'substituted': return 'Substituted';
      default: return 'Pending';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'eaten': return 'tn-status-eaten';
      case 'skipped': return 'tn-status-skipped';
      case 'substituted': return 'tn-status-substituted';
      default: return '';
    }
  };

  const calculateProgress = (current, goal) => {
    if (!goal) return 0;
    return Math.min(100, Math.round((current / goal) * 100));
  };

  // Sync All Devices — removed (FDD Architecture: all sync via HealistHealthSync push)
  const handleSyncAllDevices = async () => {
    // No-op: vendor OAuth sync-all removed in Phase 6. All data syncs via HHS.
    try {
      alert('Failed to sync devices. Please try again.');
    } finally {
      setSyncingDevices(false);
    }
  };

  // Format stat display value
  const formatStatValue = (stat, type) => {
    if (stat.prompt) return '—';
    if (!stat.has_data && type !== 'healist_score') return '—';

    switch(type) {
      case 'calories':
        return stat.value.toLocaleString();
      case 'protein':
        return `${stat.value}g`;
      case 'hydration':
        return `${stat.percent}%`;
      case 'healist_score':
        return stat.value || '—';
      default:
        return stat.value;
    }
  };

  // Get stat change/status display
  const getStatChange = (stat, type) => {
    if (stat.prompt) return stat.prompt;

    switch(type) {
      case 'calories':
        return stat.change || `${stat.percent}% of goal`;
      case 'protein':
        return stat.status || `${stat.percent}% of goal`;
      case 'hydration':
        return stat.glasses_remaining > 0
          ? `↓ ${stat.glasses_remaining} glass${stat.glasses_remaining > 1 ? 'es' : ''} remaining`
          : '✓ Goal reached!';
      case 'healist_score':
        return stat.change || stat.period || '';
      default:
        return '';
    }
  };

  // Determine if change is positive
  const isPositiveChange = (stat, type) => {
    if (stat.prompt) return false;

    switch(type) {
      case 'calories':
      case 'protein':
        return stat.percent >= 90 && stat.percent <= 110;
      case 'hydration':
        return stat.percent >= 100;
      case 'healist_score':
        return stat.change?.startsWith('+') || stat.value >= 70;
      default:
        return true;
    }
  };

  return (
    <div className="page-layout">
      <div className="page-scroll-content">
      <h1 className="text-center mb-2 dashboard-hero-title">Healist.AI Dashboard</h1>
      <p className="text-center mb-4 dashboard-hero-subtitle">Your Personalized AI Health Advisor — built around your individual unified health context</p>

      {/* STATS GRID */}
      <div className="stats-grid">
        {/* Daily Intake */}
        <div className="stat-card" onClick={() => loadStatModalData('calories')}>
          <span className="stat-card-hint">Click for Details</span>
          <div className="stat-label">Daily Intake</div>
          <div className="stat-value">
            {statsLoading ? '...' : formatStatValue(dashboardStats.daily_intake, 'calories')}
          </div>
          <div className={`stat-change ${isPositiveChange(dashboardStats.daily_intake, 'calories') ? 'positive' : 'negative'}`}>
            {statsLoading ? '' : getStatChange(dashboardStats.daily_intake, 'calories')}
          </div>
        </div>

        {/* Protein */}
        <div className="stat-card" onClick={() => loadStatModalData('protein')}>
          <span className="stat-card-hint">Click for Details</span>
          <div className="stat-label">Protein</div>
          <div className="stat-value">
            {statsLoading ? '...' : formatStatValue(dashboardStats.protein, 'protein')}
          </div>
          <div className={`stat-change ${isPositiveChange(dashboardStats.protein, 'protein') ? 'positive' : 'negative'}`}>
            {statsLoading ? '' : getStatChange(dashboardStats.protein, 'protein')}
          </div>
        </div>

        {/* Hydration */}
        <div className="stat-card" onClick={() => loadStatModalData('hydration')}>
          <span className="stat-card-hint">Click for Details</span>
          <div className="stat-label">Hydration</div>
          <div className="stat-value">
            {statsLoading ? '...' : formatStatValue(dashboardStats.hydration, 'hydration')}
          </div>
          <div className={`stat-change ${isPositiveChange(dashboardStats.hydration, 'hydration') ? 'positive' : 'negative'}`}>
            {statsLoading ? '' : getStatChange(dashboardStats.hydration, 'hydration')}
          </div>
        </div>

        {/* Healist Score */}
        <div className="stat-card" onClick={() => loadStatModalData('score')}>
          <span className="stat-card-hint">Click for Details</span>
          <div className="stat-label">Healist Score</div>
          <div className="stat-value">
            {statsLoading ? '...' : formatStatValue(dashboardStats.healist_score, 'healist_score')}
          </div>
          <div className={`stat-change ${isPositiveChange(dashboardStats.healist_score, 'healist_score') ? 'positive' : ''}`}>
            {statsLoading ? '' : getStatChange(dashboardStats.healist_score, 'healist_score')}
          </div>
        </div>
      </div>

      {/* TWO-COLUMN LAYOUT: Today's Nutrition + Quick Actions */}
      <div className="content-grid">
        {/* TODAY'S NUTRITION - Layout C (Expandable Accordion) */}
        <div className="todays-nutrition-card">
          <div className="todays-nutrition-header">
            <h3>📅 Today's Nutrition</h3>
            <div className="tn-header-actions">
              {/* Log Hydration — manual in-app water logging (opens LogHydrationModal). Re-homed here from the launcher (#81); the push-reminder one-tap log path lives separately in sw.js. */}
              <button className="tn-header-btn tn-hydration-btn" onClick={() => setShowHydrationModal(true)}>
                💧 Log Hydration
              </button>
              {hasMealPlan && todaysMeals.length > 0 && (
                <button className="tn-header-btn" onClick={handleViewAllDetails}>
                  View Today's Details
                </button>
              )}
            </div>
          </div>

          {mealsLoading ? (
            <div className="tn-loading">
              <p>Loading meals...</p>
            </div>
          ) : !hasMealPlan ? (
            <div className="tn-placeholder">
              <p className="tn-placeholder-text">
                {mealPlanPrompt || 'No meal plan active'}
              </p>
              <div className="tn-reactivate-section">
                <label className="tn-reactivate-label">
                  Select a Meal Plan to (Re)Activate Today:
                </label>
                <select
                  className="tn-reactivate-select"
                  value={selectedReactivatePlanId}
                  onChange={(e) => setSelectedReactivatePlanId(e.target.value)}
                  onFocus={() => { if (userMealPlans.length === 0) loadUserMealPlans(); }}
                >
                  <option value="">-- Choose a Meal Plan --</option>
                  {userMealPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.plan_name || plan.name || `Plan #${plan.id}`}
                    </option>
                  ))}
                  <option value="create-new">+ Create New Meal Plan</option>
                </select>
                <button
                  className="tn-btn-primary"
                  onClick={handleReactivatePlan}
                  disabled={!selectedReactivatePlanId || reactivating}
                >
                  {reactivating ? 'Activating...' : selectedReactivatePlanId === 'create-new' ? 'Create New Meal Plan' : '(Re)Activate'}
                </button>
              </div>
            </div>
          ) : todaysMeals.length === 0 ? (
            <div className="tn-placeholder">
              <p>No meals scheduled for today</p>
            </div>
          ) : (
            <>
              {/* Meal List */}
              <ul className="tn-meal-list">
                {todaysMeals.map((meal) => (
                  <li
                    key={meal.schedule_item_id}
                    className={`tn-meal-item ${expandedMeals.has(meal.schedule_item_id) ? 'expanded' : ''}`}
                  >
                    {/* Meal Summary Row */}
                    <div
                      className="tn-meal-summary"
                      onClick={() => toggleMealExpand(meal.schedule_item_id)}
                    >
                      <span className="tn-expand-icon">▶</span>
                      <div className="tn-meal-icon">{meal.icon}</div>
                      <div className="tn-meal-info">
                        <div className={`tn-meal-name ${meal.status === 'substituted' ? 'substituted' : ''} ${meal.status === 'skipped' ? 'skipped' : ''}`}>
                          {meal.name}
                        </div>
                        <div className="tn-meal-meta">
                          <span>{meal.type}</span>
                          {meal.time && <span>· {meal.time}</span>}
                          <span className={getStatusClass(meal.status)}>
                            {getStatusText(meal.status)}
                          </span>
                          {meal.half_portion && <span className="text-purple">· ½</span>}
                        </div>
                      </div>
                      <div className="tn-meal-stats">
                        <div className="tn-stat-main">{meal.calories} cal</div>
                        <div className="tn-stat-secondary">{meal.protein}g protein</div>
                      </div>
                    </div>

                    {/* Meal Details (Expanded) */}
                    <div className="tn-meal-details">
                      <div className="tn-detail-actions">
                        <button
                          className="tn-action-chip primary"
                          onClick={(e) => { e.stopPropagation(); handleViewMealDetails(meal); }}
                        >
                          Details
                        </button>
                        {meal.status !== 'skipped' && (
                          <button
                            className="tn-action-chip alternate"
                            onClick={(e) => { e.stopPropagation(); handleOpenAlternate(meal); }}
                          >
                            Alternate
                          </button>
                        )}
                        {meal.status !== 'skipped' && meal.status !== 'eaten' && (
                          <button
                            className="tn-action-chip danger"
                            onClick={(e) => { e.stopPropagation(); handleSkipMeal(meal); }}
                          >
                            Skip
                          </button>
                        )}
                        {meal.status !== 'skipped' && (
                          <div className="tn-half-toggle">
                            Half portion
                            <div
                              className={`tn-toggle-switch ${meal.half_portion ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleToggleHalfPortion(meal); }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Totals Bar */}
              <div className="tn-totals-bar">
                <div className="tn-total-item">
                  <div className="tn-total-value">
                    {mealTotals.calories.toLocaleString()} <span>/ {mealGoals.calories.toLocaleString()}</span>
                  </div>
                  <div className="tn-total-label">Calories</div>
                  <div className="tn-total-progress">
                    <div
                      className={`tn-total-progress-fill ${calculateProgress(mealTotals.calories, mealGoals.calories) >= 100 ? 'at-goal' : ''}`}
                      style={{ width: `${calculateProgress(mealTotals.calories, mealGoals.calories)}%` }}
                    />
                  </div>
                </div>
                <div className="tn-total-item">
                  <div className="tn-total-value">
                    {mealTotals.protein} <span>/ {mealGoals.protein}g</span>
                  </div>
                  <div className="tn-total-label">Protein</div>
                  <div className="tn-total-progress">
                    <div
                      className={`tn-total-progress-fill ${calculateProgress(mealTotals.protein, mealGoals.protein) >= 100 ? 'at-goal' : ''}`}
                      style={{ width: `${calculateProgress(mealTotals.protein, mealGoals.protein)}%` }}
                    />
                  </div>
                </div>
                <div className="tn-total-item">
                  <div className="tn-total-value">
                    {Math.round((mealTotals.calories / mealGoals.calories) * 100) || 0}%
                  </div>
                  <div className="tn-total-label">Complete</div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="tn-card-footer">
                <button className="tn-btn-secondary" onClick={handleOpenAddMeal}>
                  Add Meal or Snack
                </button>
                <button
                  className="tn-btn-primary"
                  onClick={handleMarkAllConsumed}
                  disabled={markingAllConsumed}
                >
                  {markingAllConsumed ? 'Marking...' : 'Mark All Consumed'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* QUICK ACTIONS */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quick Actions</h3>
          </div>
          <div className="card-body">
            <div className="quick-actions-grid">
              {/* #81 config-driven launcher. CATALOG = the personalizable action-tiles (keys match
                  launcher_service.CATALOG). Default render = the static v1 CORE order (zero regression);
                  a launcher_config reorders chosen tiles first + badges emphasized ones "For You". */}
              {(() => {
                const CATALOG = {
                  records:   { icon: '🔬', title: 'Manage My Data',      desc: 'Health Records Hub & your DOP-resolved UHC data set', onClick: () => setActiveTab('records') },
                  safety:    { icon: '🛡️', title: 'Is It Safe for Me?',  desc: 'Scan a product — checked against your full health context', onClick: () => setShowPantryItemForm(true) },
                  meals:     { icon: '🍽️', title: 'Plan My Meals',       desc: 'Recipes & meal plans built around your UHC', onClick: () => setActiveTab('nutrition') },
                  workout:   { icon: '💪', title: 'Build a Workout',     desc: 'Medication-aware fitness plans', onClick: () => setActiveTab('exercise') },
                  meds:      { icon: '💊', title: 'Check My Meds',        desc: 'Interactions, timing & supplements', onClick: () => setActiveTab('medications') },
                  vitals:    { icon: '❤️', title: 'My Vitals & Trends',  desc: 'Health metrics, devices & analytics', onClick: () => setActiveTab('health') },
                  longevity: { icon: '⏳', title: 'Live Longer',          desc: 'Longevity engine & your digital twin', onClick: () => setActiveTab('longevity') },
                  summary:   { icon: '📋', title: 'Health Summary',       desc: 'Your complete UHC at a glance', onClick: () => setShowHealthSummary(true) },
                  devices:   { icon: '⌚', title: 'My Devices',           desc: 'Connect & sync your wearables and health devices', onClick: () => setActiveTab('devices') },
                  analytics: { icon: '📊', title: 'Insights & Analytics', desc: 'Trends & insights across your health data', onClick: () => setActiveTab('analytics') },
                };
                const CORE = ['records', 'safety', 'meals', 'workout', 'meds', 'vitals', 'longevity', 'summary'];
                let ordered = CORE;
                if (launcherOrder && launcherOrder.length) {
                  const chosen = launcherOrder.filter(k => CATALOG[k]);
                  ordered = [...chosen, ...CORE.filter(k => !chosen.includes(k))];
                }
                return ordered.map(key => {
                  const t = CATALOG[key];
                  if (!t) return null;
                  const emph = launcherEmph.has(key);
                  return (
                    <div key={key} className="quick-action" onClick={t.onClick}
                         style={emph ? { borderColor: '#c8a951', boxShadow: '0 2px 10px rgba(200,169,81,.18)' } : undefined}>
                      <div className="quick-action-icon">{t.icon}</div>
                      <div className="quick-action-text">
                        <div className="quick-action-title">
                          {t.title}
                          {emph && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#8a6d16', background: '#f2e9cf', padding: '2px 6px', borderRadius: 99, letterSpacing: '.4px', verticalAlign: 'middle' }}>FOR YOU</span>}
                        </div>
                        <div className="quick-action-desc">{t.desc}</div>
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Account Logout */}
              <div className="quick-action" onClick={async () => {
                if (await confirm({
                  title: 'Log out of this device?',
                  message: 'You will be signed out here. Any other devices you are signed in on will stay signed in.',
                  confirmLabel: 'Log Out',
                })) {
                  logout(true);
                }
              }}>
                <div className="quick-action-icon">🚪</div>
                <div className="quick-action-text">
                  <div className="quick-action-title">Account Logout</div>
                  <div className="quick-action-desc">Sign out of your account</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* AI NUTRITIONIST SECTION */}
      <AINutritionistChat fetchWithAuth={fetchWithAuth} />
      {/* Stat Detail Modals */}
      {statModal.type && (
        <div className="stat-modal-overlay" onClick={closeStatModal}>
          <div className="stat-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="stat-modal-header">
              <span className="stat-modal-title">
                {statModal.type === 'hydration' && '💧 Hydration History'}
                {statModal.type === 'calories' && '🍽️ Daily Intake History'}
                {statModal.type === 'protein' && '🥩 Protein History'}
                {statModal.type === 'score' && '⭐ Healist Score Breakdown'}
              </span>
              <button className="stat-modal-close" onClick={closeStatModal}>×</button>
            </div>

            {/* Day Selector (not for score) */}
            {statModal.type !== 'score' && (
              <div className="stat-modal-day-selector">
                <span className="stat-modal-day-label">Show:</span>
                <select
                  className="stat-modal-day-select"
                  value={statModal.days}
                  onChange={(e) => handleDaysChange(parseInt(e.target.value))}
                >
                  <option value={1}>Today</option>
                  <option value={3}>Last 3 days</option>
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 180 days</option>
                </select>
              </div>
            )}

            {/* Body */}
            <div className="stat-modal-body">
              {statModal.loading ? (
                <div className="stat-modal-loading">Loading...</div>
              ) : statModal.type === 'hydration' && statModal.data ? (
                /* Hydration History */
                statModal.data.history.length === 0 ? (
                  <p className="text-center text-muted">No hydration data for this period.</p>
                ) : (
                  statModal.data.history.map((day, idx) => (
                    <div key={idx} className="history-date-group">
                      <div className="history-date-header">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      {day.entries.map((entry, entryIdx) => (
                        <div key={entryIdx} className="history-entry">
                          <span className="history-entry-time">{entry.time}</span>
                          <span>
                            <span className="history-entry-value">{entry.oz} oz</span>
                            <span className="history-entry-source">{entry.source}</span>
                          </span>
                        </div>
                      ))}
                      <div className="history-daily-total">
                        <span className="history-daily-total-label">Daily Total</span>
                        <span className="history-daily-total-value">
                          {day.total_oz} oz / {day.goal_oz} oz ({day.percent}%)
                        </span>
                      </div>
                    </div>
                  ))
                )
              ) : statModal.type === 'calories' && statModal.data ? (
                /* Calories History */
                statModal.data.history.length === 0 ? (
                  <p className="text-center text-muted">No calorie data for this period.</p>
                ) : (
                  statModal.data.history.map((day, idx) => (
                    <div key={idx} className="nutrition-day-card">
                      <div className="nutrition-day-header">
                        <span className="nutrition-day-date">
                          {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="nutrition-day-value">{day.calories.toLocaleString()} cal</span>
                      </div>
                      <div className="nutrition-progress-bar">
                        <div
                          className={`nutrition-progress-fill ${day.over ? 'over' : ''}`}
                          style={{ width: `${Math.min(day.percent, 100)}%` }}
                        ></div>
                      </div>
                      <div className="nutrition-goal-text">
                        Goal: {day.goal.toLocaleString()} cal ({day.percent}%{day.over ? ' — over' : ''})
                      </div>
                    </div>
                  ))
                )
              ) : statModal.type === 'protein' && statModal.data ? (
                /* Protein History */
                statModal.data.history.length === 0 ? (
                  <p className="text-center text-muted">No protein data for this period.</p>
                ) : (
                  statModal.data.history.map((day, idx) => (
                    <div key={idx} className="nutrition-day-card">
                      <div className="nutrition-day-header">
                        <span className="nutrition-day-date">
                          {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="nutrition-day-value">{day.protein}g</span>
                      </div>
                      <div className="nutrition-progress-bar">
                        <div
                          className={`nutrition-progress-fill ${day.over ? 'over' : ''}`}
                          style={{ width: `${Math.min(day.percent, 100)}%` }}
                        ></div>
                      </div>
                      <div className="nutrition-goal-text">
                        Goal: {day.goal}g ({day.percent}%{day.over ? ' — over' : ''})
                      </div>
                    </div>
                  ))
                )
              ) : statModal.type === 'score' && statModal.data ? (
                /* Unified Healist Score Breakdown — 8 components */
                <>
                  {statModal.data.grade && (
                    <div className="score-grade-badge">Grade {statModal.data.grade}</div>
                  )}
                  {statModal.data.components.map((comp, idx) => (
                    <div key={idx} className="score-component">
                      <div className="score-component-info">
                        <div className="score-component-name">{comp.emoji} {comp.name}</div>
                        <div className="score-component-desc">{comp.description}</div>
                      </div>
                      <div className="score-component-values">
                        {comp.data_available !== false ? (
                          <div className="score-component-score">{comp.score}</div>
                        ) : (
                          <div className="score-no-data-badge">No data</div>
                        )}
                        <div className="score-component-weight">{comp.weight}% weight</div>
                      </div>
                    </div>
                  ))}
                  <div className="score-total-box">
                    <span className="score-total-label">Total Healist Score</span>
                    <span className="score-total-value">{statModal.data.total_score}</span>
                  </div>
                </>
              ) : (
                <div className="stat-modal-loading">No data available</div>
              )}
              {/* Apple 1.4.1 (G10/G11, 2026-10-17): the "on target / below / above" judgments and
                  the Healist Score rest on published standards — one tap from here. */}
              {!statModal.loading && statModal.data && (
                <CitationsBlock
                  promptData={statModal.type === 'score' ? (statModal.data.xai_data || null) : null}
                  featureName={statModal.type === 'score' ? 'Healist Score' : 'Daily Nutrition Goals'}
                  itemTitle={statModal.type === 'score' ? 'your Healist Score' : `your ${statModal.type} goal`}
                  testId="stat-modal-citations"
                />
              )}
            </div>

            {/* Footer */}
            <div className="stat-modal-footer">
              {statModal.type === 'score'
                ? 'Calculated from last 7 days of data'
                : 'Data retained for 180 days'
              }
            </div>
          </div>
        </div>
      )}

      {/* ================= TODAY'S NUTRITION MODALS ================= */}

      {/* SUCCESS ANIMATION */}
      {showMealSuccess && (
        <div className="tn-success-overlay active">
          <div className="tn-success-icon"></div>
          <div className="tn-success-text">Success!</div>
          <div className="tn-success-detail">{mealSuccessMessage}</div>
        </div>
      )}

      {/* MARK ALL CONSUMED RESULT MODAL */}
      {showConsumedResult && consumedResult && (
        <div className="tn-modal-overlay active" onClick={() => setShowConsumedResult(false)}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>Meals Marked as Consumed</h3>
              <button className="tn-modal-close" onClick={() => setShowConsumedResult(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <div className="tn-consumed-summary">
                <strong>{consumedResult.meals_marked}</strong> meal{consumedResult.meals_marked !== 1 ? 's' : ''} marked as consumed
              </div>
              {consumedResult.pantry_depletions && consumedResult.pantry_depletions.length > 0 && (
                <div className="tn-consumed-pantry">
                  <h4>Pantry Items Decremented</h4>
                  {consumedResult.pantry_depletions.map((dep, i) => (
                    <div key={i} className="tn-consumed-pantry-item">
                      <span>{dep.meal_type}</span>
                      <span>{dep.items_decremented} item{dep.items_decremented !== 1 ? 's' : ''} used</span>
                    </div>
                  ))}
                </div>
              )}
              {(!consumedResult.pantry_depletions || consumedResult.pantry_depletions.length === 0) && consumedResult.meals_marked > 0 && (
                <p className="tn-consumed-note">No pantry items were linked to these recipes.</p>
              )}
              {consumedResult.meals_marked === 0 && (
                <p className="tn-consumed-note">All meals were already marked or no schedule items found for today.</p>
              )}
            </div>
            <div className="tn-modal-footer">
              <button className="btn btn-primary" onClick={() => setShowConsumedResult(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ALTERNATE METHOD CHOOSER */}
      {showAlternateModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowAlternateModal(false)}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>🔄 Replace This Meal</h3>
              <button className="tn-modal-close" onClick={() => setShowAlternateModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <p className="tn-modal-subtitle">Replacing: <strong>{selectedMeal?.name}</strong></p>

              <div className="tn-modal-option" onClick={() => handleSelectAlternateMethod('recipe')}>
                <div className="tn-modal-option-icon">📋</div>
                <div className="tn-modal-option-text">
                  <h4>Pick from My Recipes</h4>
                  <p>Choose a different saved recipe</p>
                </div>
              </div>

              <div className="tn-modal-option" onClick={() => handleSelectAlternateMethod('photo')}>
                <div className="tn-modal-option-icon">📷</div>
                <div className="tn-modal-option-text">
                  <h4>Take Photo of What I'm Eating</h4>
                  <p>AI analyzes your food for nutrition</p>
                </div>
              </div>

              <div className="tn-modal-option" onClick={() => handleSelectAlternateMethod('barcode')}>
                <div className="tn-modal-option-icon">🔲</div>
                <div className="tn-modal-option-text">
                  <h4>Scan Barcode</h4>
                  <p>For packaged food items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECIPE PICKER */}
      {showRecipePickerModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowRecipePickerModal(false)}>
          <div className="tn-modal wide" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>📋 Select a Recipe</h3>
              <button className="tn-modal-close" onClick={() => setShowRecipePickerModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <div className="tn-recipe-search">
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={recipeSearchTerm}
                  onChange={handleRecipeSearch}
                />
              </div>

              <div className="tn-recipe-filters">
                {['all', 'breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                  <button
                    key={type}
                    className={`tn-filter-btn ${recipeFilterType === type ? 'active' : ''}`}
                    onClick={() => handleRecipeFilterChange(type)}
                  >
                    {type === 'all' ? 'All' : type === 'breakfast' ? 'Breakfast' :
                     type === 'lunch' ? 'Lunch' : type === 'dinner' ? 'Dinner' : 'Snack'}
                  </button>
                ))}
              </div>

              <div className="tn-recipe-list">
                {recipesLoading ? (
                  <p className="tn-loading">Loading recipes...</p>
                ) : userRecipes.length === 0 ? (
                  <p className="tn-placeholder-text">No recipes found. Try a different search or filter.</p>
                ) : (
                  userRecipes.map(recipe => (
                    <div
                      key={recipe.id}
                      className={`tn-recipe-item ${selectedRecipe?.id === recipe.id ? 'selected' : ''}`}
                      onClick={() => handleSelectRecipe(recipe)}
                    >
                      <div className="tn-recipe-content">
                        <div className="tn-recipe-title">{recipe.name}</div>
                        <div className="tn-recipe-meta">
                          <span className="tn-recipe-type-tag">{recipe.meal_type || 'Meal'}</span>
                          <span className="tn-recipe-meta-item"><span className="value">{recipe.calories}</span> cal</span>
                          <span className="tn-recipe-meta-item"><span className="value">{recipe.protein}g</span></span>
                        </div>
                      </div>
                      <div className="tn-recipe-radio" />
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="tn-modal-footer">
              <button className="tn-btn-secondary" onClick={() => { setShowRecipePickerModal(false); setShowAlternateModal(true); }}>
                ← Back
              </button>
              <button
                className="tn-btn-primary"
                onClick={handleConfirmRecipeSelection}
                disabled={!selectedRecipe}
              >
                Select Recipe →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHOTO CAPTURE */}
      {showPhotoCaptureModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowPhotoCaptureModal(false)}>
          <div className="tn-modal wide" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>📷 Photo Your Meal</h3>
              <button className="tn-modal-close" onClick={() => setShowPhotoCaptureModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              {!photoResults ? (
                <>
                  <div className="tn-camera-preview">
                    {photoAnalyzing ? (
                      <div className="tn-camera-text">Analyzing with Edamam Vision API...</div>
                    ) : (
                      <>
                        <div className="tn-camera-icon">📷</div>
                        <div className="tn-camera-text">Position your meal in frame</div>
                      </>
                    )}
                  </div>
                  <div className="tn-camera-buttons">
                    <label className="tn-camera-btn upload">
                      Upload Photo <CostChip apiType="vision_meal_scan" />
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => {
                              const base64 = reader.result.split(',')[1];
                              spend('vision_meal_scan', 'Analyze this meal photo?', () => handlePhotoCapture(base64));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="tn-analyzed-image">
                    <span className="tn-analyzed-badge">✓ Analyzed</span>
                  </div>

                  <div className="tn-detected-foods">
                    <h4>🍽️ Detected: {photoResults.dish_name}</h4>
                  </div>

                  <div className="tn-nutrition-summary">
                    <h4>📊 Total Nutrition</h4>
                    <div className="tn-nutrition-grid">
                      <div className="tn-nutrition-item">
                        <div className="value">{photoResults.total_calories}</div>
                        <div className="label">Calories</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{photoResults.protein}g</div>
                        <div className="label">Protein</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{photoResults.carbs}g</div>
                        <div className="label">Carbs</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{photoResults.fat}g</div>
                        <div className="label">Fat</div>
                      </div>
                    </div>
                  </div>
                  {/* Apple 1.4.1 (G9, 2026-10-17): the nutrient estimates' sources. */}
                  <CitationsBlock featureName="Meal Photo Analysis" itemTitle={photoResults.dish_name}
                                  testId="photo-analysis-citations" />
                  <EdamamAttribution />
                </>
              )}
            </div>
            <div className="tn-modal-footer">
              <button className="tn-btn-secondary" onClick={() => { setShowPhotoCaptureModal(false); setPhotoResults(null); setShowAlternateModal(true); }}>
                ← Back
              </button>
              {photoResults && (
                <button className="tn-btn-primary" onClick={handleConfirmPhotoMeal}>
                  ✓ Use This Meal
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BARCODE LOOKUP MODAL (Today's Nutrition) */}
      {showBarcodeModal && (
        <BarcodeLookupModal
          title="Scan Barcode"
          onClose={() => setShowBarcodeModal(false)}
          fetchWithAuth={fetchWithAuth}
          onProductFound={(barcode, productData) => handleBarcodeScan(barcode, productData)}
        />
      )}

      {/* ADD MEAL/SNACK */}
      {showAddMealModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowAddMealModal(false)}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>➕ Add Meal or Snack</h3>
              <button className="tn-modal-close" onClick={() => setShowAddMealModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <p className="tn-modal-subtitle">Add an unplanned meal or snack to today's log</p>

              <div className="tn-modal-option" onClick={() => handleAddMealMethod('recipe')}>
                <div className="tn-modal-option-icon">📋</div>
                <div className="tn-modal-option-text">
                  <h4>Pick from My Recipes</h4>
                  <p>Choose from your saved recipes</p>
                </div>
              </div>

              <div className="tn-modal-option" onClick={() => handleAddMealMethod('photo')}>
                <div className="tn-modal-option-icon">📷</div>
                <div className="tn-modal-option-text">
                  <h4>Take Photo</h4>
                  <p>AI analyzes your food for nutrition</p>
                </div>
              </div>

              <div className="tn-modal-option" onClick={() => handleAddMealMethod('barcode')}>
                <div className="tn-modal-option-icon">🔲</div>
                <div className="tn-modal-option-text">
                  <h4>Scan Barcode</h4>
                  <p>For packaged food items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLOSE TODAY CONFIRMATION */}
      {showCloseDayModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowCloseDayModal(false)}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>✅ Close Today's Meal Log</h3>
              <button className="tn-modal-close" onClick={() => setShowCloseDayModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <p>This will mark all remaining planned meals as eaten and finalize your daily nutrition log.</p>

              <div className="tn-nutrition-summary">
                <h4>📊 Today's Summary</h4>
                <div className="tn-nutrition-grid">
                  <div className="tn-nutrition-item">
                    <div className="value">{mealTotals.calories}</div>
                    <div className="label">Calories</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{mealTotals.protein}g</div>
                    <div className="label">Protein</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{mealTotals.carbs}g</div>
                    <div className="label">Carbs</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{mealTotals.fat}g</div>
                    <div className="label">Fat</div>
                  </div>
                </div>
              </div>

              <p className="tn-confidence-note">
                Meals: {todaysMeals.filter(m => m.status === 'eaten').length} eaten,
                {' '}{todaysMeals.filter(m => m.status === 'skipped').length} skipped,
                {' '}{todaysMeals.filter(m => m.status === 'planned').length} pending
              </p>
            </div>
            <div className="tn-modal-footer">
              <button className="tn-btn-secondary" onClick={() => setShowCloseDayModal(false)}>
                Cancel
              </button>
              <button className="tn-btn-primary" onClick={handleConfirmCloseDay}>
                Confirm & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW ALL DETAILS */}
      {showAllDetailsModal && (
        <div className="tn-modal-overlay active" onClick={() => setShowAllDetailsModal(false)}>
          <div className="tn-modal wide" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>📋 Today's Meal Plan Details</h3>
              <button className="tn-modal-close" onClick={() => setShowAllDetailsModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body tn-modal-scrollable">
              {allDetailsLoading ? (
                <p className="tn-loading">Loading details...</p>
              ) : allMealDetails.length === 0 ? (
                <p className="tn-placeholder-text">No meals found</p>
              ) : (
                allMealDetails.map((meal, idx) => (
                  <div key={idx} className="tn-nutrition-summary">
                    <h4>{meal.meal_type?.toUpperCase()} - {meal.meal_name}</h4>
                    <div className="tn-nutrition-grid">
                      <div className="tn-nutrition-item">
                        <div className="value">{meal.calories || 0}</div>
                        <div className="label">cal</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{meal.protein || 0}g</div>
                        <div className="label">protein</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{meal.carbs || 0}g</div>
                        <div className="label">carbs</div>
                      </div>
                      <div className="tn-nutrition-item">
                        <div className="value">{meal.fat || 0}g</div>
                        <div className="label">fat</div>
                      </div>
                    </div>
                    {meal.recipe?.ingredients && (
                      <p className="tn-confidence-note">
                        <strong>Ingredients:</strong> {
                          Array.isArray(meal.recipe.ingredients)
                            ? meal.recipe.ingredients.map(i => typeof i === 'string' ? i : (i.name || i.item || '')).join(', ')
                            : String(meal.recipe.ingredients)
                        }
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="tn-modal-footer">
              <button className="tn-btn-primary" onClick={() => setShowAllDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SINGLE MEAL DETAILS */}
      {showMealDetailsModal && selectedMeal && (
        <div className="tn-modal-overlay active" onClick={() => setShowMealDetailsModal(false)}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal-header">
              <h3>📋 {selectedMeal.name}</h3>
              <button className="tn-modal-close" onClick={() => setShowMealDetailsModal(false)}>✕</button>
            </div>
            <div className="tn-modal-body">
              <div className="tn-recipe-meta">
                <span className="tn-recipe-type-tag">{selectedMeal.type}</span>
                {selectedMeal.time && <span className="tn-recipe-meta-item">at {selectedMeal.time}</span>}
              </div>

              <div className="tn-nutrition-summary">
                <h4>📊 Nutrition</h4>
                <div className="tn-nutrition-grid">
                  <div className="tn-nutrition-item">
                    <div className="value">{selectedMeal.calories}</div>
                    <div className="label">Calories</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{selectedMeal.protein}g</div>
                    <div className="label">Protein</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{selectedMeal.carbs}g</div>
                    <div className="label">Carbs</div>
                  </div>
                  <div className="tn-nutrition-item">
                    <div className="value">{selectedMeal.fat}g</div>
                    <div className="label">Fat</div>
                  </div>
                </div>
              </div>

              <p className="tn-confidence-note">
                Status: <strong>{getStatusText(selectedMeal.status)}</strong>
                {selectedMeal.half_portion && ' (Half Portion)'}
              </p>
            </div>
            <div className="tn-modal-footer">
              <button className="tn-btn-secondary" onClick={() => setShowMealDetailsModal(false)}>
                Close
              </button>
              {selectedMeal.status !== 'eaten' && (
                <button
                  className="tn-btn-primary"
                  onClick={() => { handleLogMealEaten(selectedMeal); setShowMealDetailsModal(false); }}
                >
                  ✓ Mark as Eaten
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PANTRY ITEM FORM (Quick Action → Scan Food) */}
      {showPantryItemForm && (
        <PantryItemForm
          title="Scan Food"
          onClose={() => setShowPantryItemForm(false)}
          onSave={() => setShowPantryItemForm(false)}
          fetchWithAuth={fetchWithAuth}
        />
      )}
      </div>
    </div>
  );
}

export default DashboardTab;
